import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Megaphone, Activity, Plus, Pin, ThumbsUp, MessageCircle, 
  Clock, User, ExternalLink, Edit, Trash2, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Announcement types with icons and colors
const ANNOUNCEMENT_TYPES = {
  general: { label: 'General', icon: '📢', color: 'bg-blue-100 text-blue-700' },
  news: { label: 'News', icon: '📰', color: 'bg-purple-100 text-purple-700' },
  update: { label: 'Update', icon: '🔄', color: 'bg-green-100 text-green-700' },
  alert: { label: 'Alert', icon: '⚠️', color: 'bg-amber-100 text-amber-700' },
  celebration: { label: 'Celebration', icon: '🎉', color: 'bg-pink-100 text-pink-700' }
};

// Activity type icons
const ACTIVITY_ICONS = {
  deal_won: '🏆',
  deal_created: '💼',
  task_completed: '✅',
  contact_added: '👤',
  ticket_resolved: '🎧',
  default: '📌'
};

export const CompanyAnnouncementsSection = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({
    title: '',
    content: '',
    announcement_type: 'general',
    priority: 'normal',
    is_pinned: false,
    scheduled_for: ''
  });

  const getToken = () => localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${getToken()}` };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setIsAdmin(['admin', 'sales_manager', 'marketing_manager'].includes(user.role));
  }, []);

  const fetchAnnouncements = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (isAdmin) params.append('include_scheduled', 'true');
      const res = await axios.get(`${API}/api/preferences/announcements?${params}`, { headers });
      setAnnouncements(res.data);
    } catch (err) {
      console.error('Failed to fetch announcements:', err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const createAnnouncement = async () => {
    if (!form.title || !form.content) {
      toast.error('Title and content are required');
      return;
    }
    
    try {
      const payload = { ...form };
      // Only include scheduled_for if it's set
      if (!payload.scheduled_for) {
        delete payload.scheduled_for;
      }
      await axios.post(`${API}/api/preferences/announcements`, payload, { headers });
      fetchAnnouncements();
      setModalOpen(false);
      setForm({ title: '', content: '', announcement_type: 'general', priority: 'normal', is_pinned: false, scheduled_for: '' });
      toast.success(form.scheduled_for ? 'Announcement scheduled' : 'Announcement published');
    } catch (err) {
      toast.error('Failed to create announcement');
    }
  };

  const deleteAnnouncement = async (id) => {
    if (!confirm('Delete this announcement?')) return;
    try {
      await axios.delete(`${API}/api/preferences/announcements/${id}`, { headers });
      fetchAnnouncements();
      toast.success('Announcement deleted');
    } catch (err) {
      toast.error('Failed to delete announcement');
    }
  };

  const reactToAnnouncement = async (id, emoji) => {
    try {
      await axios.post(`${API}/api/preferences/announcements/${id}/react?emoji=${encodeURIComponent(emoji)}`, {}, { headers });
      fetchAnnouncements();
    } catch (err) {
      console.error('Failed to react:', err);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getReactionCount = (reactions, emoji) => {
    return reactions?.[emoji]?.length || 0;
  };

  return (
    <Card data-testid="company-announcements">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle className="text-lg flex items-center gap-2">
            <Megaphone className="h-5 w-5" />
            Company Announcements
          </CardTitle>
          <CardDescription>Latest news and updates from the company</CardDescription>
        </div>
        {isAdmin && (
          <Button size="sm" onClick={() => setModalOpen(true)} data-testid="create-announcement-btn">
            <Plus className="h-4 w-4 mr-1" />
            Post
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[300px]">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : announcements.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Megaphone className="h-12 w-12 mx-auto text-slate-200 mb-2" />
              <p>No announcements yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {announcements.map((announcement) => {
                const typeInfo = ANNOUNCEMENT_TYPES[announcement.announcement_type] || ANNOUNCEMENT_TYPES.general;
                const isScheduled = announcement.scheduled_for && new Date(announcement.scheduled_for) > new Date();
                return (
                  <div key={announcement.id} className={`p-4 rounded-lg border ${announcement.is_pinned ? 'bg-amber-50 border-amber-200' : isScheduled ? 'bg-blue-50 border-blue-200' : 'bg-white'}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {announcement.is_pinned && <Pin className="h-4 w-4 text-amber-500" />}
                          {isScheduled && <Clock className="h-4 w-4 text-blue-500" />}
                          <Badge className={typeInfo.color}>
                            {typeInfo.icon} {typeInfo.label}
                          </Badge>
                          {announcement.priority === 'high' && (
                            <Badge variant="destructive">High Priority</Badge>
                          )}
                          {isScheduled && (
                            <Badge variant="outline" className="text-blue-600 border-blue-300">
                              Scheduled
                            </Badge>
                          )}
                        </div>
                        <h4 className="font-semibold">{announcement.title}</h4>
                        <p className="text-sm text-muted-foreground mt-1">{announcement.content}</p>
                        <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {announcement.creator?.first_name} {announcement.creator?.last_name}
                          </span>
                          {isScheduled ? (
                            <span className="flex items-center gap-1 text-blue-600">
                              <Clock className="h-3 w-3" />
                              Scheduled: {new Date(announcement.scheduled_for).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                            </span>
                          ) : (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatDate(announcement.created_at)}
                            </span>
                          )}
                        </div>
                        {/* Reactions */}
                        {!isScheduled && (
                          <div className="flex items-center gap-2 mt-3">
                            {['👍', '❤️', '🎉'].map(emoji => (
                              <button
                                key={emoji}
                                onClick={() => reactToAnnouncement(announcement.id, emoji)}
                                className={`px-2 py-1 rounded-full text-sm border hover:bg-slate-100 ${
                                  getReactionCount(announcement.reactions, emoji) > 0 ? 'bg-slate-100' : ''
                                }`}
                              >
                                {emoji} {getReactionCount(announcement.reactions, emoji) > 0 && getReactionCount(announcement.reactions, emoji)}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {isAdmin && (
                        <Button variant="ghost" size="icon" onClick={() => deleteAnnouncement(announcement.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </CardContent>

      {/* Create Announcement Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Announcement</DialogTitle>
            <DialogDescription>Post a company-wide announcement</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                placeholder="Announcement title"
                value={form.title}
                onChange={(e) => setForm({...form, title: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea
                placeholder="Write your announcement..."
                value={form.content}
                onChange={(e) => setForm({...form, content: e.target.value})}
                rows={4}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={form.announcement_type} onValueChange={(v) => setForm({...form, announcement_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ANNOUNCEMENT_TYPES).map(([key, val]) => (
                      <SelectItem key={key} value={key}>{val.icon} {val.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({...form, priority: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Schedule for later (optional)</Label>
              <Input
                type="datetime-local"
                value={form.scheduled_for}
                onChange={(e) => setForm({...form, scheduled_for: e.target.value ? new Date(e.target.value).toISOString() : ''})}
                min={new Date().toISOString().slice(0, 16)}
              />
              <p className="text-xs text-muted-foreground">Leave empty to publish immediately</p>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_pinned"
                checked={form.is_pinned}
                onChange={(e) => setForm({...form, is_pinned: e.target.checked})}
              />
              <Label htmlFor="is_pinned">Pin this announcement</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={createAnnouncement}>{form.scheduled_for ? 'Schedule' : 'Publish'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export const CompanyActivityFeed = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  const getToken = () => localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${getToken()}` };

  const fetchActivities = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/preferences/activity-feed?limit=10`, { headers });
      setActivities(res.data);
    } catch (err) {
      console.error('Failed to fetch activities:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActivities();
    // Refresh every minute
    const interval = setInterval(fetchActivities, 60000);
    return () => clearInterval(interval);
  }, [fetchActivities]);

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <Card data-testid="company-activity-feed">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Activity className="h-5 w-5" />
          Company Activity
        </CardTitle>
        <CardDescription>Recent activity across the organization</CardDescription>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[300px]">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : activities.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Activity className="h-12 w-12 mx-auto text-slate-200 mb-2" />
              <p>No recent activity</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activities.map((activity) => (
                <div key={activity.id} className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50">
                  <span className="text-xl">{ACTIVITY_ICONS[activity.activity_type] || ACTIVITY_ICONS.default}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{activity.title}</p>
                    {activity.description && (
                      <p className="text-xs text-muted-foreground">{activity.description}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      {activity.user_name} • {formatTime(activity.created_at)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export const CustomAppLinks = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    url: '',
    icon: '🔗',
    category: 'general'
  });

  const getToken = () => localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${getToken()}` };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setIsAdmin(user.role === 'admin');
  }, []);

  const fetchLinks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/preferences/app-links`, { headers });
      setLinks(res.data);
    } catch (err) {
      console.error('Failed to fetch app links:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  const createLink = async () => {
    if (!form.name || !form.url) {
      toast.error('Name and URL are required');
      return;
    }
    
    try {
      await axios.post(`${API}/api/preferences/app-links`, form, { headers });
      fetchLinks();
      setModalOpen(false);
      setForm({ name: '', description: '', url: '', icon: '🔗', category: 'general' });
      toast.success('App link created');
    } catch (err) {
      toast.error('Failed to create app link');
    }
  };

  const deleteLink = async (id) => {
    if (!confirm('Delete this app link?')) return;
    try {
      await axios.delete(`${API}/api/preferences/app-links/${id}`, { headers });
      fetchLinks();
      toast.success('App link deleted');
    } catch (err) {
      toast.error('Failed to delete app link');
    }
  };

  const ICONS = ['🔗', '📊', '📈', '💬', '📧', '📁', '🗂️', '⚙️', '🔧', '📱'];

  return (
    <Card data-testid="custom-app-links">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle className="text-lg flex items-center gap-2">
            <ExternalLink className="h-5 w-5" />
            Quick Links
          </CardTitle>
          <CardDescription>Company apps and resources</CardDescription>
        </div>
        {isAdmin && (
          <Button size="sm" variant="outline" onClick={() => setModalOpen(true)} data-testid="add-app-link-btn">
            <Plus className="h-4 w-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-4 text-muted-foreground">Loading...</div>
        ) : links.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground text-sm">
            {isAdmin ? 'Add quick links for your team' : 'No app links configured'}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {links.map((link) => (
              <a
                key={link.id}
                href={link.url}
                target={link.open_in_new_tab ? '_blank' : '_self'}
                rel="noopener noreferrer"
                className="flex items-center gap-2 p-3 rounded-lg border hover:bg-slate-50 transition-colors group"
              >
                <span className="text-xl">{link.icon}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{link.name}</p>
                  {link.description && (
                    <p className="text-xs text-muted-foreground truncate">{link.description}</p>
                  )}
                </div>
                <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100" />
                {isAdmin && (
                  <button
                    onClick={(e) => { e.preventDefault(); deleteLink(link.id); }}
                    className="opacity-0 group-hover:opacity-100"
                  >
                    <Trash2 className="h-3 w-3 text-red-500" />
                  </button>
                )}
              </a>
            ))}
          </div>
        )}
      </CardContent>

      {/* Create App Link Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add App Link</DialogTitle>
            <DialogDescription>Add a quick link for your team</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Icon</Label>
              <div className="flex gap-2 flex-wrap">
                {ICONS.map(icon => (
                  <button
                    key={icon}
                    type="button"
                    className={`p-2 text-xl rounded border ${form.icon === icon ? 'bg-slate-100 border-slate-300' : ''}`}
                    onClick={() => setForm({...form, icon})}
                  >
                    {icon}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                placeholder="Google Drive"
                value={form.name}
                onChange={(e) => setForm({...form, name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>URL</Label>
              <Input
                placeholder="https://drive.google.com"
                value={form.url}
                onChange={(e) => setForm({...form, url: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Input
                placeholder="Company shared drive"
                value={form.description}
                onChange={(e) => setForm({...form, description: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="productivity">Productivity</SelectItem>
                  <SelectItem value="communication">Communication</SelectItem>
                  <SelectItem value="tools">Tools</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={createLink}>Add Link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default { CompanyAnnouncementsSection, CompanyActivityFeed, CustomAppLinks };
